import { STS_MODULE_ID } from "../../../../utlis/measurementModules";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import {
  MOBILITY_REPORT_SHARED,
  PERSON_DASHBOARD,
  TEST_HISTORY,
} from "../../../../routes/routerPath";
import { getSeniorReport } from "../../../../services/v5Api";
import {
  fetchMobilityReport,
  patchMobilityReport,
} from "../../../../services/mobilityReportApi";
import { createShareableReport } from "../../../../services/dynamicActionPlanApi";
import { userInfoFetch } from "../../../../utlis/commonFunction";
import { getCurrentLocale } from "../../../../utlis/locale";
import { rememberMobilityReportSnapshot } from "../../../../utlis/mobilityReportSnapshot";
import { useT } from "../../../../i18n/useT";
import { getPersonTerminology } from "../../../../utlis/personTerminology";
import NoDataFound from "../../../common/NoDataFound";
import { buildMobilityReportSrcDoc } from "./iframeTemplate";
import {
  buildEngineDataFromServerReport,
  engineModeForReport,
} from "./serverReportAdapter";
import "./MobilityReport.scss";

// Both report endpoints have existed with a response envelope
// ({ status, message, data: report }) as well as a bare report object. Keep
// metadata from the envelope when unwrapping it: some deployments return the
// resident thumbnail beside `data`, rather than inside the report itself.
const unwrapReportPayload = (response) => {
  const nested = response?.data;
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) {
    return response;
  }
  return { ...response, ...nested };
};

// The backend v2 document carries only a private storage key for the photo.
// Until it returns a readable photo URL, take the thumbnail URL from the
// legacy report response (display data only; nothing else is read from it).
const legacyThumbnail = (response) => {
  const body = unwrapReportPayload(response) || {};
  return (
    body.thumbnailPath ||
    body.tumbnailPath ||
    body.thumbnail_path ||
    body.tumbnail_path ||
    null
  );
};

// The mobility report no longer lets the user pick a report type. Instead the
// mode is derived from the account's customer_type (from the users API), mirroring
// the tenant aliases used by the action plan. Keys are the normalized customer_type
// (lowercase, spaces/hyphens -> underscore); values are the iframe's mode ids.
const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M19 12H5m0 0l7-7m-7 7l7 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MobilityReport = () => {
  const t = useT("mobilityReport");
  const tSts = useT("sts");
  const tCommon = useT("common");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userInfo = useMemo(() => userInfoFetch(), []);
  // customer_type lives on the logged-in staff user, not the senior. PersonDashboard
  // caches that record in sessionStorage["staffDetails"]; prefer it for mode
  // resolution and fall back to the senior's user info. Mirrors the action plan's
  // resolveActionPlanTenant(staffDetails || userInfo).
  const staffDetails = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("staffDetails") || "null");
    } catch {
      return null;
    }
  }, []);
  const iframeRef = useRef(null);
  const personLabels = useMemo(
    () => getPersonTerminology(staffDetails || userInfo),
    [staffDetails, userInfo],
  );

  const seniorId =
    searchParams.get("seniorId") ||
    searchParams.get("seniorUuid") ||
    userInfo?.id;
  const moduleId = searchParams.get("moduleId") || "4";
  // An exact report revision (notification links); otherwise the latest.
  const exactReportId = searchParams.get("reportId") || "";
  const exactRevision = searchParams.get("revision") || "";
  // Per-module report cards already provide the backend-hosted report URL.
  // Pass it into the iframe so Share → Preview and the send API use it.
  const reportPreviewUrl = searchParams.get("reportUrl") || "";
  const activityId =
    searchParams.get("activity_id") || searchParams.get("activityId") || "";
  // Backend v2 report document: the only source of the report's results.
  const [report, setReport] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const reportRef = useRef({ document: null, etag: null });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const unsupportedStsReport = Number(moduleId) === STS_MODULE_ID;

  const shareSeniorUuid = report?.context?.senior_id || seniorId;
  const reportLocale = getCurrentLocale();
  const reportData = useMemo(
    () =>
      report ? buildEngineDataFromServerReport(report, { photoUrl }) : null,
    [photoUrl, report],
  );

  const handleBack = useCallback(() => {
    // Return to wherever the report was opened from (action tab, metrics,
    // history, …) rather than forcing everyone back to the metrics tab.
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    // Deep-link fallback: nothing to pop, so land on the dashboard.
    const params = new URLSearchParams(window.location.search);
    params.delete("moduleId");
    params.delete("reportUrl");
    params.set("tab", "metrics");
    if (seniorId) params.set("seniorId", seniorId);
    navigate(`${PERSON_DASHBOARD}?${params.toString()}`);
  }, [navigate, seniorId]);

  const handleViewTestHistory = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    if (seniorId) params.set("seniorUuid", seniorId);
    const query = params.toString();
    navigate(query ? `${TEST_HISTORY}?${query}` : TEST_HISTORY);
  }, [navigate, seniorId]);

  useEffect(() => {
    let cancelled = false;

    const loadReport = async () => {
      if (Number(moduleId) === STS_MODULE_ID) {
        setReport(null);
        setError("");
        setIsLoading(false);
        return;
      }
      if (!seniorId) {
        setError(t("senior_missing"));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");
      try {
        // Backend-written sentences (summary, why-flagged, notes) in the
        // report language; the v95 engine translates its own copy.
        const exact = exactReportId && exactRevision;
        const [result, legacy] = await Promise.all([
          fetchMobilityReport(seniorId, {
            locale: ["ja", "zh"].includes(reportLocale) ? reportLocale : "en",
            activityId: activityId || undefined,
            ...(exact
              ? { reportId: exactReportId, revision: exactRevision }
              : {}),
          }),
          getSeniorReport(seniorId).catch(() => null),
        ]);
        if (!cancelled) {
          reportRef.current = { document: result.document, etag: result.etag };
          setReport(result.document);
          setPhotoUrl(legacyThumbnail(legacy));
        }
      } catch (err) {
        console.error("Error loading mobility report:", err);
        if (!cancelled) {
          // 404 = "No test reports found for senior" — not a failure, just an
          // empty state. Leave report null so the "No report available" view
          // renders instead of the error/retry view.
          if (err?.response?.status !== 404) {
            setError(
              err?.response?.data?.message || err?.message || t("load_failed"),
            );
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [
    activityId,
    exactReportId,
    exactRevision,
    moduleId,
    reportLocale,
    seniorId,
  ]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === "seniorlife:mobility-report-back") {
        handleBack();
      } else if (
        event.data?.type === "seniorlife:mobility-report-test-history"
      ) {
        handleViewTestHistory();
      } else if (event.data?.type === "seniorlife:mobility-share-modal") {
        setShareModalOpen(Boolean(event.data.open));
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleBack, handleViewTestHistory]);

  useEffect(() => {
    document.body.classList.toggle("mobility-share-modal-open", shareModalOpen);
    return () => document.body.classList.remove("mobility-share-modal-open");
  }, [shareModalOpen]);

  // Backend bridge for the report iframe. The report runs in an isolated srcDoc
  // iframe with no access to the app's auth token, so remark authoring posts
  // request messages here; we run the authed v5Api service and post correlated
  // responses back. Sharing no longer needs an authenticated recipient bridge.
  useEffect(() => {
    const respond = (requestId, patch) => {
      const frame = iframeRef.current?.contentWindow;
      if (!frame || !requestId) return;
      frame.postMessage(
        { type: "seniorlife:bridge-response", requestId, ...patch },
        "*",
      );
    };

    const handleBridge = async (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const msg = event.data;
      if (!msg || msg.type !== "seniorlife:bridge-request") return;
      const { requestId, action, payload } = msg;
      try {
        let data = null;
        switch (action) {
          // Edits go to the backend report (server-issued assessment and
          // revision, ETag + idempotency). The backend recomputes the report;
          // the new document replaces the old one for later saves and shares.
          case "fim:save":
          case "remark:save": {
            const { document, etag } = reportRef.current;
            if (!document) throw new Error("The report is not loaded.");
            const remarksTarget = document.remarks || {};
            // FIM edits go to the assessment that owns the FIM items.
            const itemId = String(payload?.itemId || "");
            const fimTarget =
              (document.edit_targets || []).find(
                (entry) =>
                  entry.current && (entry.fim_items || []).includes(itemId),
              ) ||
              (document.edit_targets || []).find(
                (entry) =>
                  String(entry.assessment_id) ===
                  String(document.fim?.assessment_id),
              ) ||
              {};
            const change =
              action === "fim:save"
                ? {
                    assessment_id: fimTarget.assessment_id,
                    expected_assessment_revision: fimTarget.assessment_revision,
                    fim_overrides: [
                      {
                        item_id: itemId,
                        fim_override: payload?.fimOverride ?? null,
                      },
                    ],
                  }
                : {
                    assessment_id: remarksTarget.assessment_id,
                    expected_assessment_revision:
                      remarksTarget.assessment_revision,
                    remarks: {
                      [payload?.kind === "staff" ? "staff" : "person"]:
                        payload?.text || "",
                    },
                  };
            const result = await patchMobilityReport(
              seniorId,
              document,
              etag,
              change,
            );
            reportRef.current = {
              document: result.document,
              etag: result.etag,
            };
            // The recomputed report goes back to the iframe for display.
            data = {
              kind: payload?.kind,
              text: payload?.text || "",
              report: result.document,
            };
            break;
          }
          // Persist the printable report payload behind a public slug (same
          // shareable-report API as the action plan) and return a short URL the
          // iframe uses for Preview / Copy Link / Share. The report endpoint is
          // unauthenticated, so no session token is ever placed in the link.
          case "report:create-share": {
            const shareBody = await createShareableReport({
              seniorId: shareSeniorUuid,
              version: "v1",
              data: {
                kind: "mobility_report",
                mode: payload?.mode || "Safety",
                locale: reportLocale,
                payload: payload?.payload
                  ? { ...payload.payload, locale: reportLocale }
                  : null,
              },
            });
            const slug = shareBody?.slug || "";
            const url = slug
              ? `${window.location.origin}${MOBILITY_REPORT_SHARED.replace(
                  ":slug",
                  encodeURIComponent(slug),
                )}`
              : "";
            // Latest shared report → the Patient Summary's "Full Mobility
            // Report" link (no report ID needed).
            rememberMobilityReportSnapshot(shareSeniorUuid, slug);
            if (seniorId && seniorId !== shareSeniorUuid) {
              rememberMobilityReportSnapshot(seniorId, slug);
            }
            data = { slug, url };
            break;
          }
          default:
            respond(requestId, { ok: false, error: "Unknown action" });
            return;
        }
        respond(requestId, { ok: true, data });
      } catch (err) {
        respond(requestId, {
          ok: false,
          error:
            err?.response?.data?.message || err?.message || "Request failed.",
          status: err?.response?.status || 0,
        });
      }
    };

    window.addEventListener("message", handleBridge);
    return () => window.removeEventListener("message", handleBridge);
  }, [reportLocale, seniorId, shareSeniorUuid]);

  const srcDoc = useMemo(() => {
    if (!reportData) return "";
    // When the backend falls back to another language (e.g. a locale not
    // enabled for the market), render the whole report in that language
    // instead of mixing translated chrome with backend sentences.
    const engineLocale = ["en", "ja", "zh"].includes(
      report?.context?.content_locale,
    )
      ? report.context.content_locale
      : reportLocale;
    // The document says which report it is; never inferred from the account.
    const mode = engineModeForReport(report);
    return buildMobilityReportSrcDoc(
      reportData,
      // Same patient key the adapter used for the engine data.
      String(report?.context?.senior_id ?? seniorId),
      mode,
      reportPreviewUrl,
      getPersonTerminology(staffDetails || userInfo, engineLocale),
      engineLocale,
      false,
      activityId,
      report,
    );
  }, [
    activityId,
    personLabels,
    report,
    reportData,
    reportLocale,
    reportPreviewUrl,
    seniorId,
    staffDetails,
    userInfo,
  ]);

  const backButton = (
    <button
      type="button"
      className="mobility-report-exit"
      onClick={handleBack}
      aria-label={tCommon("back")}
    >
      <BackIcon />
    </button>
  );

  return (
    <div className="mobility-report-route">
      {isLoading ? (
        <div className="mobility-report-state">
          {backButton}
          <CircularProgress size={32} />
        </div>
      ) : unsupportedStsReport ? (
        <div className="mobility-report-state">
          {backButton}
          <NoDataFound icon="mobility" message={tSts("report_unavailable")} />
        </div>
      ) : error ? (
        <div className="mobility-report-state">
          {backButton}
          <NoDataFound
            icon="mobility"
            message={t("load_error_title")}
            description={error}
            actionLabel={t("retry")}
            onAction={() => window.location.reload()}
          />
        </div>
      ) : !report ? (
        <div className="mobility-report-state">
          {backButton}
          <NoDataFound
            icon="mobility"
            message={t("empty_title")}
            description={t("empty_description", {
              person: personLabels.singularLower,
            })}
          />
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          title={t("frame_title")}
          className="mobility-report-frame"
          allow="web-share"
          srcDoc={srcDoc}
        />
      )}
    </div>
  );
};

export default MobilityReport;
